import React from 'react'
import { BiLoaderAlt, BiTime, BiX } from 'react-icons/bi'

export interface TaskSuggestionCardProps {
  taskName: string
  reason: string
  scheduleLabel?: string
  loading?: boolean
  onSetUp: () => void
  onDismiss: () => void
}

/**
 * Inline banner after an agent reply when the backend suggests scheduling a recurring task.
 */
export default function TaskSuggestionCard({
  taskName,
  reason,
  scheduleLabel,
  loading,
  onSetUp,
  onDismiss,
}: TaskSuggestionCardProps) {
  return (
    <div
      className="tw-mt-3 tw-border-t tw-border-slate-100 tw-pt-3 tw-text-left"
      role="region"
      aria-label="Scheduled task suggestion"
    >
      <div className={`tw-flex tw-items-start tw-gap-2 ${loading ? 'tw-animate-pulse' : ''}`}>
        <div className="tw-mt-0.5 tw-flex tw-shrink-0 tw-text-slate-400" aria-hidden>
          {loading ? (
            <BiLoaderAlt className="tw-h-5 tw-w-5 tw-animate-spin" />
          ) : (
            <BiTime className="tw-h-5 tw-w-5" />
          )}
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-600">
            {loading ? 'Checking if this is worth scheduling…' : taskName || 'Schedule this as a recurring task'}
          </p>
          {!loading && reason ? (
            <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-leading-snug tw-text-slate-500">{reason}</p>
          ) : null}
          {!loading && scheduleLabel ? (
            <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-slate-400">Suggested cadence: {scheduleLabel}</p>
          ) : null}
          {!loading ? (
            <div className="tw-mt-2">
              <button
                type="button"
                onClick={onSetUp}
                className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-blue-600 tw-transition-colors hover:tw-text-blue-700"
              >
                Set up schedule
              </button>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="tw--mt-0.5 tw-flex tw-shrink-0 tw-rounded-md tw-p-1 tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-100 hover:tw-text-slate-600"
          aria-label="Dismiss suggestion"
        >
          <BiX className="tw-h-5 tw-w-5" aria-hidden />
        </button>
      </div>
    </div>
  )
}
